import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const comparisonRows = [
  {
    label: "Deliverable",
    cpo: "Measurable business outcomes: growth, retention, revenue",
    consultant: "Reports, recommendations, and slide decks",
  },
  {
    label: "Accountability",
    cpo: "Accountable for results. Embedded in your team.",
    consultant: "Accountable for deliverables. External to your team.",
  },
  {
    label: "Duration",
    cpo: "Flexible engagement tied to outcomes",
    consultant: "Fixed project scope with defined end date",
  },
  {
    label: "Depth",
    cpo: "Deep. Makes decisions, ships product, coaches the team.",
    consultant: "Surface. Analyzes, recommends, presents.",
  },
  {
    label: "Outcome",
    cpo: "Your product drives growth. Your team is stronger.",
    consultant: "You have a strategy document. Implementation is on you.",
  },
];

const faqs = [
  {
    question:
      "What is the difference between FractionalCPO and a product management consultant?",
    answer:
      "A product management consultant delivers analysis, frameworks, and recommendations. FractionalCPO embeds with your team as an interim executive, makes decisions, ships product, coaches your people, and is accountable for measurable business outcomes. Consultants tell you what to do. We do it with you.",
  },
  {
    question:
      "Why do PE-backed companies choose embedded leadership over consulting?",
    answer:
      "Speed and accountability. PE timelines demand results in months, not years. Consulting engagements produce reports that take additional months to implement, often requiring another vendor to execute. Embedded fractional leadership starts delivering impact from week one because they're part of the team, not outside it.",
  },
  {
    question: "What happens after a consulting engagement ends?",
    answer:
      "Often, very little changes. The report sits on a shelf. The team didn't build the muscle to execute on the recommendations because the consultant wasn't embedded long enough to drive change. With embedded fractional leadership, the changes are built into the team's DNA. The capability stays after the engagement ends.",
  },
];

const FractionalCPOvsConsultant = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Fractional CPO vs Management Consultant: Embedded Leadership vs Advisory"
        description="Compare fractional CPO leadership with product management consulting. Learn why PE-backed technology companies choose embedded leadership over advisory engagements."
        path="/fractional-cpo-vs-consultant"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Fractional CPO vs Management Consultant: Embedded Leadership vs Advisory",
          "url": "https://fractionalcpo.com/fractional-cpo-vs-consultant",
          "dateModified": "2026-06-17",
          "description": "Compare fractional CPO leadership with product management consulting. Learn why PE-backed technology companies choose embedded leadership over advisory engagements.",
          "isPartOf": { "@type": "WebSite", "url": "https://fractionalcpo.com" }
        }}
      />

      {/* Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Comparison
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            Fractional CPO vs{" "}
            <span className="text-[#002178] font-medium">
              Management Consultant
            </span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-xl md:text-2xl leading-relaxed font-medium text-[#1a1a1a] mb-6">
              Embedded leadership vs. advisory. Results vs. reports.
            </p>
            <p className="text-lg text-black/60 leading-relaxed mb-4">
              Management consultants deliver analysis and recommendations. They
              observe from the outside, present findings, and move on to the
              next project.
            </p>
            <p className="text-lg text-black/60 leading-relaxed">
              <Link
                to="/what-is-a-fractional-cpo"
                className="text-[#002178] underline underline-offset-4 hover:text-[#002178]/80"
              >
                FractionalCPO
              </Link>{" "}
              embeds with your team. We make decisions, ship product, coach
              your people, and own the outcomes. The difference is
              accountability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            How they{" "}
            <span className="text-[#002178] font-medium">compare</span>
          </motion.h2>

          {/* Desktop Table */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:block overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-black/10">
                  <th className="text-left py-4 pr-6 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[20%]" />
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-[#002178] font-medium w-[40%]">
                    Fractional CPO
                  </th>
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[40%]">
                    Management Consultant
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                  >
                    <td className="py-5 pr-6 text-sm font-medium text-black/60">
                      {row.label}
                    </td>
                    <td className="py-5 px-4 text-sm font-medium text-[#1a1a1a]">
                      {row.cpo}
                    </td>
                    <td className="py-5 px-4 text-sm text-black/50">
                      {row.consultant}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-6">
            {comparisonRows.map((row, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                className="border-b border-black/10 pb-6"
              >
                <p className="text-sm font-medium text-black/40 uppercase tracking-[0.15em] mb-3">
                  {row.label}
                </p>
                <div className="space-y-3">
                  <div className="bg-[#002178]/[0.04] p-3 rounded">
                    <p className="text-xs text-[#002178] font-medium mb-1">
                      Fractional CPO
                    </p>
                    <p className="text-sm font-medium">{row.cpo}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-black/40 font-medium mb-1">
                      Management Consultant
                    </p>
                    <p className="text-sm text-black/60">{row.consultant}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why PE-Backed Companies Choose Embedded Leadership */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                Why PE-backed companies choose{" "}
                <span className="text-[#002178] font-medium">
                  embedded leadership
                </span>
              </motion.h2>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                <p className="text-lg text-black/60 leading-relaxed">
                  PE timelines don't wait for recommendations to be
                  implemented. The fund has a thesis, the portfolio company
                  needs to grow, and the clock is ticking.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  Embedded leadership delivers speed. No months of discovery.
                  No handoff gap between "recommendation" and "execution." The
                  leader who diagnoses the problem is the same leader who
                  fixes it.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  And accountability is built in. A consultant is accountable
                  for the quality of their analysis. An embedded leader is
                  accountable for results.
                </p>
              </motion.div>
            </div>

            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                What happens after{" "}
                <span className="text-[#002178] font-medium">
                  a consulting engagement
                </span>
              </motion.h2>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                <p className="text-lg text-black/60 leading-relaxed">
                  Too often, nothing changes. The report was thorough. The
                  recommendations were sound. But the team didn't build the
                  capability to execute on them, and the consultant has moved
                  on.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  With embedded fractional leadership, change is built in. The
                  leader coaches the team, builds new processes, and develops
                  the organizational muscle to sustain the improvements long
                  after the engagement ends.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  You don't just get a strategy. You get a team that can
                  execute it.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              Ready for results,{" "}
              <span className="font-medium">not reports?</span>
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/70 leading-relaxed mb-8"
            >
              See how our embedded approach compares. Read our{" "}
              <Link
                to="/case-study"
                className="text-white underline underline-offset-4 hover:text-white/90"
              >
                case study
              </Link>{" "}
              or start a conversation.
            </motion.p>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <button
                onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
                className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
              >
                Let's chat
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default FractionalCPOvsConsultant;
